import React from 'react';
import {
  Banner as AtomsBanner,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import { Grid } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { Form } from '@fortress-technology-solutions/fortress-component-library/Organisms';
import { AlertCriticalIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';

import messages from './messages';

export default function PetFeeModal({
  handleSubmit,
  // When provided, dismissal closes the fee modal without saving the Pet form.
  onCancel,
  reactHookFormProps,
  formFields,
  open,
  petsToCharge = [],
  petRentConfigured = false,
  // The backend creates the charge from the Pet Rent amount on the LEASE, not
  // from the property's configured fee, so the banner has to read the same
  // value or it promises a charge that is never created.
  leaseMonthlyPetRent = null,
  // Disable actions during submission to prevent duplicate charges.
  disabled = false,
  intl,
}) {
  // One-time pet fees/deposits the user can choose to post. Pet rent is
  // recurring and surfaced as informational text only (see petRentConfigured).
  const hasFees = formFields.length > 0;
  // A blank or $0.00 lease amount means the backend will skip the charge. When
  // the amount is not known here, keep the original wording rather than
  // asserting something we cannot back up.
  const willChargePetRent =
    leaseMonthlyPetRent === null || Number(leaseMonthlyPetRent) > 0;
  // Apply the same reset for button, backdrop, and Escape dismissal.
  const handleClose = () => {
    reactHookFormProps.reset();
    if (onCancel) {
      onCancel();
      return;
    }
    handleSubmit();
  };
  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={intl.formatMessage(messages.modalTitle)}
      icon={<AlertCriticalIcon />}
      actionsProps={[
        {
          variant: 'text',
          children: intl.formatMessage(messages.cancel),
          onClick: handleClose,
          disabled,
        },
        {
          children: intl.formatMessage(messages.confirm),
          onClick: handleSubmit,
          disabled,
        },
      ]}
      ContentProps={{
        sx: {
          width: {
            xs: 'calc(100% - 32px)',
            sm: 651,
          },
          pt: 2,
        },
      }}
    >
      <Grid container spacing={2} mb={2}>
        {hasFees && (
          <Grid item xs={12}>
            <AtomsBanner
              status="warning"
              message={intl.formatMessage(messages.petFeeChargeWarning)}
              sx={{ width: '100%' }}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <Typography variant="labelLarge">
            {intl.formatMessage(messages.noticedAddedPets)}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography>
            {petsToCharge.map((pet) => pet.name).join(', ')}
          </Typography>
        </Grid>
        {petRentConfigured && (
          <Grid item xs={12}>
            <AtomsBanner
              status={willChargePetRent ? 'info' : 'warning'}
              message={intl.formatMessage(
                willChargePetRent
                  ? messages.petRentInfo
                  : messages.petRentNotChargedInfo,
              )}
              sx={{ width: '100%' }}
            />
          </Grid>
        )}
        {hasFees && (
          <Grid item xs={12} mb={2}>
            <Typography variant="labelLarge">
              {intl.formatMessage(messages.postPetFeesPrompt)}
            </Typography>
          </Grid>
        )}
      </Grid>
      {hasFees && (
        <Form ReactHookFormProps={reactHookFormProps} fields={formFields} />
      )}
    </Modal>
  );
}
